/**
 * Patient portal client and session, V3.
 *
 * A separate client from `lib/api.ts`, for the same reason the server has
 * separate middleware: a patient token must never be attached to a clinic
 * request, and a clinic token must never be attached to a portal request.
 *
 * Sharing `lib/api.ts` would put both tokens in one store under one key. A
 * clinician checking their own results at home would then have two sessions
 * fighting over `mcos.access`, and whichever wrote last would decide what the
 * other one saw. Different keys, different client, no collision.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const BASE = '/api/v1/portal';
const ACCESS_KEY = 'mcos.portal.access';
const REFRESH_KEY = 'mcos.portal.refresh';

export class PortalApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'PortalApiError';
  }
}

export const portalTokens = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string): void {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  // One rotation, however many requests hit 401 at once. Firing several would
  // look like token replay to the server, which revokes the whole family.
  refreshInFlight ??= (async () => {
    const refreshToken = portalTokens.refresh;
    if (!refreshToken) return false;
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) return false;
      const data = (await response.json()) as {
        accessToken: string;
        refreshToken: string;
      };
      portalTokens.set(data.accessToken, data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so concurrent callers all observe the result.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

interface PortalRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Internal: prevents an endless refresh loop. */
  retried?: boolean;
}

export async function portalRequest<T>(
  path: string,
  options: PortalRequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  const access = portalTokens.access;
  if (access) headers.authorization = `Bearer ${access}`;
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  const response = await fetch(`${BASE}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (response.status === 401 && !options.retried && portalTokens.refresh) {
    if (await refreshSession()) {
      return portalRequest<T>(path, { ...options, retried: true });
    }
    portalTokens.clear();
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } })?.error;
    throw new PortalApiError(
      response.status,
      error?.code ?? 'ERROR',
      error?.message ?? 'Something went wrong.',
    );
  }

  return payload as T;
}

/** Save a PDF the patient is entitled to (their plan record), with the same refresh as any other request. */
/** The bytes of a signed-in resource, for a photo shown from memory rather than from a link (MS-014). */
export async function portalBlob(path: string, retried = false): Promise<Blob> {
  const access = portalTokens.access;
  const response = await fetch(`${BASE}${path}`, { headers: access ? { authorization: `Bearer ${access}` } : {} });
  if (response.status === 401 && !retried && portalTokens.refresh && (await refreshSession())) return portalBlob(path, true);
  if (!response.ok) throw new PortalApiError(response.status, 'ERROR', 'That photo could not be loaded.');
  return response.blob();
}

/** A multipart upload (a message photo). The JSON helper cannot carry a file. */
export async function portalUpload<T>(path: string, form: FormData, retried = false): Promise<T> {
  const access = portalTokens.access;
  const response = await fetch(`${BASE}${path}`, { method: 'POST', headers: access ? { authorization: `Bearer ${access}` } : {}, body: form });
  if (response.status === 401 && !retried && portalTokens.refresh && (await refreshSession())) return portalUpload<T>(path, form, true);
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new PortalApiError(response.status, data?.error?.code ?? 'ERROR', data?.error?.message ?? 'That photo could not be added.');
  return data as T;
}

export async function portalDownload(path: string, filename: string, retried = false): Promise<void> {
  const access = portalTokens.access;
  const response = await fetch(`${BASE}${path}`, { headers: access ? { authorization: `Bearer ${access}` } : {} });
  if (response.status === 401 && !retried && portalTokens.refresh && (await refreshSession())) {
    return portalDownload(path, filename, true);
  }
  if (!response.ok) throw new PortalApiError(response.status, 'ERROR', 'That document could not be downloaded.');
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/* ------------------------------------------------------------------ *
 * Session
 * ------------------------------------------------------------------ */

export interface PortalMe {
  patient: {
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
  };
  card: {
    brand: string | null;
    last4: string | null;
    expMonth: number | null;
    expYear: number | null;
  } | null;
  subscription: {
    status: string;
    cycleMonths: number;
    installmentsPaid: number;
    enrolledAt: string;
    nextChargeAt: string | null;
    renewalPromptedAt: string | null;
    renewalConfirmedAt: string | null;
  } | null;
  renewalDue: boolean;
}

interface PortalSession {
  status: 'loading' | 'anonymous' | 'authenticated';
  me: PortalMe | null;
  login: (email: string, password: string, clinic?: string) => Promise<void>;
  setPassword: (token: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
}

const PortalContext = createContext<PortalSession | null>(null);

export function PortalProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<PortalSession['status']>('loading');
  const [me, setMe] = useState<PortalMe | null>(null);

  const load = useCallback(async () => {
    if (!portalTokens.access && !portalTokens.refresh) {
      setStatus('anonymous');
      setMe(null);
      return;
    }
    try {
      setMe(await portalRequest<PortalMe>('/me'));
      setStatus('authenticated');
    } catch {
      portalTokens.clear();
      setMe(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const value = useMemo<PortalSession>(
    () => ({
      status,
      me,
      async login(email, password, clinic) {
        const data = await portalRequest<{ accessToken: string; refreshToken: string }>(
          '/auth/login',
          { method: 'POST', body: { email, password, clinic } },
        );
        portalTokens.set(data.accessToken, data.refreshToken);
        await load();
      },
      async setPassword(token, password) {
        const data = await portalRequest<{ accessToken: string; refreshToken: string }>(
          '/auth/set-password',
          { method: 'POST', body: { token, password } },
        );
        portalTokens.set(data.accessToken, data.refreshToken);
        await load();
      },
      async logout() {
        const refreshToken = portalTokens.refresh;
        if (refreshToken) {
          await portalRequest('/auth/logout', {
            method: 'POST',
            body: { refreshToken },
          }).catch(() => undefined);
        }
        portalTokens.clear();
        setMe(null);
        setStatus('anonymous');
      },
      reload: load,
    }),
    [status, me, load],
  );

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

export function usePortal(): PortalSession {
  const context = useContext(PortalContext);
  if (!context) throw new Error('usePortal must be used inside <PortalProvider>');
  return context;
}
